"use strict";
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __classPrivateFieldSet = (this && this.__classPrivateFieldSet) || function (receiver, privateMap, value) {
    if (!privateMap.has(receiver)) {
        throw new TypeError("attempted to set private field on non-instance");
    }
    privateMap.set(receiver, value);
    return value;
};
var __classPrivateFieldGet = (this && this.__classPrivateFieldGet) || function (receiver, privateMap) {
    if (!privateMap.has(receiver)) {
        throw new TypeError("attempted to get private field on non-instance");
    }
    return privateMap.get(receiver);
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
var _conferenceService, _presentation;
Object.defineProperty(exports, "__esModule", { value: true });
exports.FilePresentationService = void 0;
const Service_1 = require("../Service");
const events_1 = require("../../events");
const VoxeetApi_1 = __importDefault(require("../../api/VoxeetApi"));
/**
 * @ignore
 */
var FilePresentationServiceEvents;
(function (FilePresentationServiceEvents) {
    FilePresentationServiceEvents["Converted"] = "converted";
    FilePresentationServiceEvents["ConversionProgress"] = "conversionProgress";
    FilePresentationServiceEvents["Started"] = "started";
    FilePresentationServiceEvents["Updated"] = "updated";
    FilePresentationServiceEvents["Stopped"] = "stopped";
})(FilePresentationServiceEvents || (FilePresentationServiceEvents = {}));
/**
 * @ignore
 */
const MAXIMUM_FILE_SIZE = 262144000;
/**
 * The FilePresentationService allows presenting files during a conference. The Dolby.io Communications APIs service converts the user-provided file into multiple pages, as images, accessible through the [image](#image) method.
 *
 * **The file presentation workflow:**
 *
 * **1.** The presenter calls the [convert](#convert) method to upload and convert a file.
 *
 * **2.** The presenter receives the [conversionProgress](#conversionprogress) event that informs that the file conversion is in progress.
 *
 * **3.** The presenter receives [converted](#converted) event when the file conversion is finished.
 *
 * **4.** The presenter calls the [start](#start) method to start presenting the file.
 *
 * **5.** The presenter and the viewers receive the [started](#started) event that informs that the file presentation starts. Receiving the [started](#started) event should trigger calling the [image](#image) method to download the converted file and display the proper page of the file by retrieving the individual images.
 *
 * **6.** The application is responsible for coordinating the page flip between the local and the presented files. The presenter calls the [update](#update) method to inform the service to send the updated page number to the participants.
 *
 * **7.** The presenter and viewers receive the [updated](#updated) event with the current page number. Receiving the [updated](#updated) event should trigger calling the [image](#image) method to display the proper page of the file by retrieving the individual images.
 *
 * **8.** The presenter may call the [thumbnail](#thumbnail) method to obtain thumbnail images of the file and implement a carousel control for the presenting user to flip pages locally.
 *
 * **9.** The presenter calls the [stop](#stop) method to end the file presentation.
 *
 * **10.** The presenter and the viewers receive the [stopped](#stopped) event to inform about the end of the file presentation.
 *
 * The following diagram presents the easiest workflow for presenting files during a conference.
 *
 * <br>
 *
 * <img src="../assets/Web_file_presentation.png" alt="The file presentation diagram" title="The file presentation diagram" width="700"/>
 *
 * The [current](#current) accessor allows the participants to receive information about the current state of the file presentation.
 *
 * <br>
 *
 * @noInheritDoc
 */
let FilePresentationService = class FilePresentationService extends Service_1.BaseConferenceService {
    /**
     * @ignore
     * @param sdk
     * @param conferenceService
     */
    constructor(sdk, conferenceService) {
        super(sdk);
        _conferenceService.set(this, void 0);
        /**
         * The presented file
         */
        _presentation.set(this, null);
        __classPrivateFieldSet(this, _conferenceService, conferenceService);
    }
    /**
     * Converts the user-provided file into multiple pages, as images, that can be shared during the file presentation. The file is uploaded as FormData.
     *
     * Supported file formats are:
     *
     * - doc/docx (Microsoft Word)
     * - ppt/pptx
     * - pdf
     *
     * After conversion, the files are broken into individual images with maximum resolution capped at **2560x1600**.
     *
     * When the file is converted and ready for the presentation, the application receives the [converted](#converted) event.
     *
     * Only the converted files can be shared during conferences.
     *
     * @param file - The file that the presenter wants to share during the conference.
     */
    convert(file) {
        if (file.size > MAXIMUM_FILE_SIZE) {
            throw new Error('File size exceeds 250 MB, the maximum file size.');
        }
        let data = new FormData();
        data.append('file', file);
        return VoxeetApi_1.default.file.convert({ data: data });
    }
    /**
     * Converts a file to a specific URL.
     * @param filename - Name of the file
     * @param url - Url of the file
     * @ignore
     */
    convertUrl(filename, url) {
        return VoxeetApi_1.default.file.urlConvert({ data: { filename: filename, url: url } });
    }
    /**
     * Starts a file presentation. The Dolby.io Communications APIs allow presenting only the converted files.
     * @param file - The converted file that the presenter wants to share during the conference.
     */
    start(file) {
        return VoxeetApi_1.default.fileshare
            .start({ conferenceId: this._conferenceId, data: { fileId: file.id, imageCount: file.imageCount } })
            .then((r) => r.data);
    }
    /**
     * Informs the service to send the updated page number to the conference participants.
     * @param page - The page number that corresponds to the page that should be presented.
     */
    update(page) {
        return __awaiter(this, void 0, void 0, function* () {
            this.checkPresentation();
            const res = yield VoxeetApi_1.default.fileshare.update({ conferenceId: this._conferenceId, data: { fileId: __classPrivateFieldGet(this, _presentation).id, position: page } });
            return res.data;
        });
    }
    /**
     * Stops the file presentation.
     */
    stop() {
        return __awaiter(this, void 0, void 0, function* () {
            this.checkPresentation();
            const res = yield VoxeetApi_1.default.fileshare.stop({ conferenceId: this._conferenceId, data: { fileId: __classPrivateFieldGet(this, _presentation).id } });
            return res.data;
        });
    }
    /**
     * Downloads and displays locally the presented file by retrieving URLs of the individual images.
     * @param page - The number of the presented page. Files that do not have any pages, for example jpg images, require setting the value of the page parameter to `0`.
     * @param fileId - The file ID that allows presenting files without joining a conference.
     */
    image(page, fileId) {
        let id = fileId;
        if (!id) {
            this.checkPresentation();
            id = __classPrivateFieldGet(this, _presentation).id;
        }
        return Promise.resolve(`${VoxeetApi_1.default.host}/v1/files/${id}/converted/${page}?token=${VoxeetApi_1.default.jwtToken}&hashId=${VoxeetApi_1.default.getHashId()}`);
    }
    /**
     * Provides the thumbnail's URL that refers to a specific page of the presented file.
     * @param page - The number of the presented page. Files that do not include any pages, for example jpg images, require setting the value of this parameter to `0`.
     * @param fileId - The file ID that allows presenting files without joining a conference.
     */
    thumbnail(page, fileId) {
        let id = fileId;
        if (!id) {
            this.checkPresentation();
            id = __classPrivateFieldGet(this, _presentation).id;
        }
        return Promise.resolve(`${VoxeetApi_1.default.host}/v1/files/${id}/converted/${page}/thumbnail/?token=${VoxeetApi_1.default.jwtToken}&hashId=${VoxeetApi_1.default.getHashId()}`);
    }
    checkPresentation() {
        if (!__classPrivateFieldGet(this, _presentation)) {
            throw new Error('File Presentation not stated');
        }
    }
    onFileConverted(e) {
        this.emit(FilePresentationServiceEvents.Converted, e.files[0]);
    }
    onFileConversionProgress(event) {
        this.emit(FilePresentationServiceEvents.ConversionProgress, event.fileConversion);
    }
    onFilePresentationStarted(e) {
        __classPrivateFieldSet(this, _presentation, e.file);
        __classPrivateFieldGet(this, _presentation).owner = __classPrivateFieldGet(this, _conferenceService).participants.get(e.file.ownerId);
        this.emit(FilePresentationServiceEvents.Started, __classPrivateFieldGet(this, _presentation));
    }
    onFilePresentationUpdated(e) {
        __classPrivateFieldGet(this, _presentation).position = e.file.position;
        this.emit(FilePresentationServiceEvents.Updated, __classPrivateFieldGet(this, _presentation));
    }
    onFilePresentationStopped(e) {
        const presentation = __classPrivateFieldGet(this, _presentation);
        __classPrivateFieldSet(this, _presentation, null);
        this.emit(FilePresentationServiceEvents.Stopped, presentation);
    }
    /**
     * Returns information about the current state of the file presentation. Use this accessor if you wish to receive information that is available in the [FilePresentation](model/filepresentation) object, such as the [file ID](doc:js-client-sdk-model-filepresentation#id), [the number of images](doc:js-client-sdk-model-filepresentation#optional-imagecount) in the presentation, information about the [file owner](doc:js-client-sdk-model-filepresentation#owner), or the current [position](doc:js-client-sdk-model-filepresentation#optional-position) in the presentation. For example, use the following code to ask about the file owner:
     *
     * ```javascript
     * VoxeetSDK.filePresentation.current.owner;
     * ```
     */
    get current() {
        return __classPrivateFieldGet(this, _presentation);
    }
};
_conferenceService = new WeakMap(), _presentation = new WeakMap();
__decorate([
    Service_1.ConferenceNotNull()
], FilePresentationService.prototype, "start", null);
__decorate([
    Service_1.ConferenceNotNull()
], FilePresentationService.prototype, "update", null);
__decorate([
    Service_1.ConferenceNotNull()
], FilePresentationService.prototype, "stop", null);
FilePresentationService = __decorate([
    Service_1.Subscribe([
        events_1.EventType.FileConversionProgress,
        events_1.EventType.FileConverted,
        events_1.EventType.FilePresentationStarted,
        events_1.EventType.FilePresentationStopped,
        events_1.EventType.FilePresentationUpdated,
    ])
], FilePresentationService);
exports.FilePresentationService = FilePresentationService;
